/**
 * Utility functions for prerender.io integration
 */

/**
 * Sets the prerender ready flag to true after ensuring DOM updates are flushed
 * This should only be called when the page content is fully loaded and rendered
 */
export const setPrerenderReady = () => {
  if (typeof window !== 'undefined') {
    // Use requestAnimationFrame to ensure DOM updates are flushed
    requestAnimationFrame(() => {
      window.prerenderReady = true;
      
      // Log for debugging (only in prerender environment)
      if (isPrerender()) {
        console.log('Prerender ready flag set to true');
      }
    });
  }
};

/**
 * Checks if the current environment is likely a crawler/bot
 * This can be used to optimize loading behavior for crawlers
 */
export const isCrawler = () => {
  if (typeof window === 'undefined') return false;
  
  const userAgent = navigator.userAgent.toLowerCase();
  const crawlerPatterns = [
    'googlebot',
    'bingbot',
    'slurp',
    'duckduckbot',
    'baiduspider',
    'yandexbot',
    'facebookexternalhit',
    'twitterbot',
    'linkedinbot',
    'whatsapp',
    'telegrambot',
    'prerender',
    'headless',
    'phantom',
    'selenium',
    'puppeteer'
  ];
  
  return crawlerPatterns.some(pattern => userAgent.includes(pattern));
};

/**
 * Specifically checks if the current environment is prerender.io
 * Based on the known prerender.io user agents:
 * - Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) HeadlessChrome/89.0.4389.82 Safari/537.36 Prerender (+https://github.com/prerender/prerender)
 * - Mozilla/5.0 (Linux; Android 11; Pixel 5) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/89.0.4389.72 Mobile Safari/537.36 Prerender (+https://github.com/prerender/prerender)
 */
export const isPrerender = () => {
  if (typeof window === 'undefined') return false;
  
  const userAgent = navigator.userAgent;
  
  // Check for the specific prerender.io signature
  return userAgent.includes('Prerender (+https://github.com/prerender/prerender)');
};

/**
 * Checks if the current environment is a mobile prerender.io bot
 * This helps distinguish between desktop and mobile prerender requests
 */
export const isMobilePrerender = () => {
  if (typeof window === 'undefined') return false;
  
  const userAgent = navigator.userAgent;
  
  // Check for mobile prerender.io signature
  return userAgent.includes('Prerender (+https://github.com/prerender/prerender)') && 
         userAgent.includes('Mobile Safari');
};

/**
 * Sets prerender ready with a small delay to ensure content is visible
 * Use this when you need to ensure content is painted before signaling ready
 */
export const setPrerenderReadyWithDelay = (delay = 100) => {
  if (typeof window !== 'undefined') {
    setTimeout(() => {
      requestAnimationFrame(() => {
        window.prerenderReady = true;
        
        // Log for debugging (only in prerender environment)
        if (isPrerender()) {
          console.log('Prerender ready flag set to true (with delay)');
        }
      });
    }, delay);
  }
};

/**
 * Optimized prerender ready function that handles mobile vs desktop prerender differently
 * Mobile prerender might need slightly more time for content to be fully rendered
 */
export const setPrerenderReadyOptimized = () => {
  if (typeof window !== 'undefined') {
    const isMobile = isMobilePrerender();
    const delay = isMobile ? 150 : 100; // Slightly longer delay for mobile prerender
    
    setTimeout(() => {
      requestAnimationFrame(() => {
        window.prerenderReady = true;
        
        // Log for debugging (only in prerender environment)
        if (isPrerender()) {
          console.log(`Prerender ready flag set to true (${isMobile ? 'mobile' : 'desktop'})`);
        }
      });
    }, delay);
  }
};

/**
 * Debug function to log current user agent and prerender detection status
 * This can be called during development to verify detection is working
 */
export const debugPrerenderDetection = () => {
  if (typeof window !== 'undefined') {
    console.log('=== Prerender Detection Debug ===');
    console.log('User Agent:', navigator.userAgent);
    console.log('Is Crawler:', isCrawler());
    console.log('Is Prerender:', isPrerender());
    console.log('Is Mobile Prerender:', isMobilePrerender());
    console.log('Prerender Ready Flag:', window.prerenderReady);
    console.log('================================');
  }
};
